'use client';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { doNothing } from '@/libs/utils/function';
import Container from '@/components/primitives/container';
import { XIcon } from 'lucide-react';
import Typography from '@/components/primitives/typography';

import styles from './styles.module.scss';

interface ToastProviderProps {
  children: React.ReactNode;
}

interface ToastContextProps {
  showToast: (content: React.ReactNode, duration?: number) => void;
  hide: (id: string) => void;
}

interface ToastItemProps {
  id: string;
  duration?: number;
  content: React.ReactNode;
  closing?: boolean;
  onClose: (id: string) => void;
}

export const ToastItem = (props: ToastItemProps) => {
  const { id = '', duration = 3000, closing = false, onClose } = props;
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (ref.current) {
      const height = ref.current.scrollHeight;

      ref.current.style.setProperty('--toast-height', `${height}px`);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onClose.call(null, id);
    }, duration);

    return () => clearTimeout(timeout);
  }, [duration, id, onClose]);

  const handleAnimationEnd = () => {
    if(!closing) return;
    onClose?.call(null, id);
  };

  return <div
    ref={ref}
    className={`${styles.toastItem} ${closing ? styles.closing : ''}`}
    role="status"
    onAnimationEnd={handleAnimationEnd}
  >
    <Container rootClassName={styles.contentsRoot} className={styles.contents}>
      <Typography weight="normal">
        {props.content}
      </Typography>
    </Container>
    {/* 앱의 회전 progress 테두리는 성능 측정 전까지 쓰지 않고, 정적인 2px 테두리만 둔다. */}
    <button
      type="button"
      className={`${styles.close} focus-ring`}
      aria-label="닫기"
      onClick={() => onClose?.call(null, id)}>
      <XIcon width={24} height={24} />
    </button>
  </div>;
};

export const ToastContext = React.createContext<ToastContextProps>({
  showToast: doNothing,
  hide: doNothing,
});

export const ToastProvider = (props: ToastProviderProps) => {
  const { children } = props;

  const [toasts, setToasts] = React.useState<Omit<ToastItemProps, 'onClose'>[]>([]);

  const showToast = useCallback((content: React.ReactNode, duration?: number) => {
    console.trace('토스트 호출');
    const id = crypto.randomUUID();
    setToasts((prevToasts) => [...prevToasts, { id, content, duration }]);
  }, []);

  const hide = useCallback((id: string) => {
    setToasts((prevToasts) =>
      prevToasts.map((toast) =>
        toast.id === id ? { ...toast, closing: true } : toast,
      ),
    );
  }, []);

  const handleClose = useCallback((id: string) => {
    hide(id);
  }, [hide]);

  return <ToastContext.Provider value={{
    showToast,
    hide,
  }}>
    {children}
    <div className={styles.root}>
      {toasts.map(toast => <ToastItem key={toast.id} {...toast} onClose={handleClose} />)}
    </div>
  </ToastContext.Provider>;
};

export const useToast = () => {
  return useContext(ToastContext);
};
